import {
  listen,
  isChangeAccountMessage,
  isData,
  sendLocal,
  projectChangedMessage,
} from "@sinch/bus";
import { isValidUrl, isString } from "@sinch/feature-flags";
import { useFeatureFlag } from "@sinch/feature-flags/react";
import { useEffect, useCallback } from "react";
import { config as globalConfig } from "../config";
import { setExceptionMonitoringProjectContext } from "../exceptionMonitoring";
import { safeRetrieveValue } from "../helpers/safeRetrieveValue";
import { useLoginToken } from "../hooks/useLogin";
import { useUser } from "../user-context";

import {
  setProjectIdSessionStorage,
  headerName as projectIdHeader,
} from "../x-project-id";

const changeActiveProject = async (token: string, project_id: number) => {
  const config: RequestInit = {
    method: "PUT",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      [projectIdHeader]: project_id.toString(),
    },
    body: JSON.stringify({ project_id }),
  };

  /**
   * Terminology change:
   * Channels in MCP => Project in Sinch Engage
   */
  return fetch(`${globalConfig.API_BASE_URL}/api/v16/agent/channel`, config);
};

/**
 * Reset the authorization extension cache, so that it points to the correct
 * project.
 */
const useAuthExtensionLogin = () => {
  const apiGatewayUrl = useFeatureFlag(
    "apiGatewayUrl",
    isValidUrl,
    "https://api.appengage.sinch.com"
  );

  return useCallback(
    (token: string, projectId: number) => {
      const config: RequestInit = {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          [projectIdHeader]: projectId.toString(),
        },
        redirect: "manual",
      };
      // We have some CORS issues here, but the request still seem to be
      // honored. We use .allSettled to ignore the errors but wait for it to be
      // completed. Hopefully at some point we should be able to remove this
      // code.
      return Promise.allSettled([fetch(`${apiGatewayUrl}/auth/login`, config)]);
    },
    [apiGatewayUrl]
  );
};

// Currently is made for legacy engage
export const useChangeProjectHandler = () => {
  const loginToken = useLoginToken();
  const authExtLogin = useAuthExtensionLogin();
  const { activeProject: currentProjectId } = useUser();

  const changeProject = useCallback(
    async (newProjectId: number) => {
      if (!loginToken) throw new Error("token not found");
      await changeActiveProject(loginToken.token, newProjectId);
      const sub = safeRetrieveValue(loginToken.parsedToken, "sub", isString);
      setProjectIdSessionStorage(newProjectId, sub);
      setExceptionMonitoringProjectContext(String(newProjectId));
      await authExtLogin(loginToken.token, newProjectId);

      const parsedProjectId = parseInt(currentProjectId || "");
      sendLocal(
        projectChangedMessage({
          from: {
            projectId: isNaN(parsedProjectId) ? undefined : parsedProjectId,
          },
          to: { projectId: newProjectId },
        })
      );
    },
    [authExtLogin, currentProjectId, loginToken]
  );

  useEffect(() => {
    const unsub = listen((message) => {
      if (isData(message)) {
        const projectId = parseInt(message.payload.accountId);
        if (typeof projectId === "number" && projectId > 0) {
          changeProject(projectId);
        }
      }
    }, isChangeAccountMessage);

    return unsub;
  }, [changeProject]);
};
